/**
 * resolveString — safe translation fallback helper for `@puffin-core/login-shell`.
 *
 * Given an optional consumer-supplied translator function `t` and a string key,
 * returns the first usable value from:
 *
 *   1. `t(key)` — if `t` is a function and the return value is a usable string.
 *   2. `DEFAULT_LOGIN_STRINGS[key]` — the shipped English default.
 *   3. `undefined` — only when the key is absent from both.
 *
 * A "usable" value from `t(key)` is any value that is NOT one of:
 *   - `undefined` / `null` — consumer has no translation for this key
 *   - `''` (empty string) — treated as missing; no locale in this codebase
 *     legitimately requires a blank label
 *   - the key itself — react-i18next's default return for missing keys
 *
 * This replaces the pre-2026-04-21 pattern
 *   `const translate = (key) => (t ? t(key) : DEFAULT_LOGIN_STRINGS[key]);`
 * which rendered empty DOM text whenever a consumer supplied a `t` that had
 * gaps. See `CRITICAL-FIXES/parcelpuffin-uat/SELF-ASSESSMENT-2026-04-20-LOGIN-ALIGNMENT-FAILURE.md`.
 *
 * @module @puffin-core/login-shell/strings/resolveString
 */

import { createStringResolver } from '@puffin-core/i18n-bootstrap/strings-kit';

import { DEFAULT_LOGIN_STRINGS } from './en.js';

/**
 * @param {((key: string, params?: Object) => string|undefined|null)|undefined|null} t
 *   Optional consumer translator. When absent, defaults are used. When
 *   `params` is provided, it is passed through as the second argument (for
 *   react-i18next-style interpolation — caller must still handle
 *   `{placeholder}` interpolation of the returned default template itself,
 *   since defaults use single-curly syntax and this util does not interpolate).
 * @param {string} key  Translation key — see `DEFAULT_LOGIN_STRINGS` for the
 *   canonical set.
 * @param {Object} [params]  Optional interpolation params forwarded to the
 *   consumer's `t`. Ignored for the default-strings fallback.
 * @returns {string|undefined} The resolved string, or `undefined` only when
 *   `key` is not defined in `DEFAULT_LOGIN_STRINGS` and `t` did not supply it.
 */
// I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared implementation. The invariant is
// now proved by BEHAVIOUR — `loginStringsWiring.contract.test.js` CALLS this through five arms plus
// an arity probe, rather than grepping this file for the literal `/v !== key/`.
export const resolveString = createStringResolver(DEFAULT_LOGIN_STRINGS);
